export interface ChmsOrganizationSettings {
  [key: string]: unknown;
  allowed_email_domains?: string[];
  allowed_email_domains_enabled?: boolean;
  integrations?: string[] | Record<string, unknown>;
  plan?: string;
  mrr?: number;
  seat_limit?: number;
  setup_progress?: Record<string, boolean>;
  financeDefaults?: Record<string, unknown>;
  next_billing_date?: string;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  payrollPeriod?: Record<string, unknown>;
  // Password policy + MFA grace anchor (W6-4). Stored flat alongside the MFA keys
  // and normalized below so they are first-class rather than untyped passthrough.
  mfaGracePeriodDays?: number;
  mfaEnforcedAt?: string;
  minPasswordLength?: number;
  requireUppercase?: boolean;
  requireNumbers?: boolean;
  requireSpecialChars?: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

const toOptionalStringArray = (value: unknown): string[] | undefined =>
  Array.isArray(value)
    ? toStringArray(value).length > 0 || value.length === 0
      ? toStringArray(value)
      : []
    : undefined;

const toOptionalStringRecordArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  if (value.every((entry) => typeof entry === "string")) {
    return value.filter((item): item is string => typeof item === "string");
  }

  return undefined;
};

const toRecord = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? value : {};

const toOptionalCampusList = (
  value: unknown,
): { name: string; address_line1?: string; address_line2?: string; city?: string; state?: string; postal_code?: string; }[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const normalized = value
    .map((campus): {
      name: string;
      address_line1?: string;
      address_line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
    } | null => {
      if (!isRecord(campus)) return null;
      const name = typeof campus.name === "string" ? campus.name : "";
      const city = typeof campus.city === "string" ? campus.city : undefined;
      const state = typeof campus.state === "string" ? campus.state : undefined;
      const postal_code = typeof campus.postal_code === "string" ? campus.postal_code : undefined;
      const address_line1 =
        typeof campus.address_line1 === "string" ? campus.address_line1 : undefined;
      const address_line2 =
        typeof campus.address_line2 === "string" ? campus.address_line2 : undefined;

      if (!name && !city && !state && !postal_code && !address_line1 && !address_line2) {
        return null;
      }

      return {
        name: name || "Unnamed campus",
        address_line1,
        address_line2,
        city,
        state,
        postal_code,
      };
    })
    .filter((campus): campus is
      { name: string; address_line1?: string; address_line2?: string; city?: string; state?: string; postal_code?: string } => Boolean(campus));

  return normalized.length > 0 ? normalized : [];
};

const toOptionalNumberArray = (value: unknown): number[] | undefined =>
  Array.isArray(value)
    ? value.every((entry) => typeof entry === "number")
      ? (value as number[])
      : undefined
    : undefined;

const toOptionalRecord = (value: unknown): Record<string, unknown> | undefined =>
  isRecord(value) ? value : undefined;

const toOptionalBoolean = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

const toOptionalString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const toOptionalNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const toOptionalObject = (value: unknown): unknown => (isRecord(value) ? value : undefined);

const toOnboardingProfile = (value: unknown): {
  size?: "small" | "medium" | "large";
  denomination?: string;
  ministries?: string[];
  serviceTimes?: string[];
  completedAt?: string;
} | undefined => {
  if (!isRecord(value)) return undefined;

  return {
    size:
      value.size === "small" || value.size === "medium" || value.size === "large"
        ? value.size
        : undefined,
    denomination: toOptionalString(value.denomination),
    ministries: toOptionalStringRecordArray(value.ministries),
    serviceTimes: toOptionalStringRecordArray(value.serviceTimes),
    completedAt: toOptionalString(value.completedAt),
  };
};

const toAutomationNotifications = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value)) return undefined;

  const eventRecipients = toOptionalStringRecordArray(value.eventRecipients);
  const thresholdRecipients = toOptionalStringRecordArray(value.thresholdRecipients);
  const failureRateThreshold = toOptionalNumber(value.failureRateThreshold);
  const delayMinutesThreshold = toOptionalNumber(value.delayMinutesThreshold);
  const dailyVolumeThreshold = toOptionalNumber(value.dailyVolumeThreshold);

  return {
    ...(eventRecipients ? { eventRecipients } : {}),
    ...(thresholdRecipients ? { thresholdRecipients } : {}),
    ...(failureRateThreshold !== undefined ? { failureRateThreshold } : {}),
    ...(delayMinutesThreshold !== undefined ? { delayMinutesThreshold } : {}),
    ...(dailyVolumeThreshold !== undefined ? { dailyVolumeThreshold } : {}),
  };
};

const toPayrollPeriod = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value)) return undefined;

  return {
    ...(toOptionalString(value.start) ? { start: value.start } : {}),
    ...(toOptionalString(value.end) ? { end: value.end } : {}),
  };
};

const toPublicGivingKiosk = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value)) return undefined;

  const enabled = toOptionalBoolean(value.enabled);
  const fullScreenEnabled = toOptionalBoolean(value.fullScreenEnabled);
  const idleResetEnabled = toOptionalBoolean(value.idleResetEnabled);
  const idleResetSeconds = toOptionalNumber(value.idleResetSeconds);

  return {
    ...(enabled !== undefined ? { enabled } : {}),
    ...(fullScreenEnabled !== undefined ? { fullScreenEnabled } : {}),
    ...(idleResetEnabled !== undefined ? { idleResetEnabled } : {}),
    ...(idleResetSeconds !== undefined ? { idleResetSeconds } : {}),
  };
};

const toCampaignFundTracking = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value)) return undefined;

  return value;
};

const toPeopleStatuses = (
  value: unknown,
): Array<{ id: string; label: string; category: string } | string> | undefined => {
  if (!Array.isArray(value)) return undefined;

  const normalized = value
    .map((entry) => {
      if (typeof entry === "string") return entry;
      if (!isRecord(entry)) return null;
      if (typeof entry.id !== "string" || typeof entry.label !== "string") return null;

      return {
        id: entry.id,
        label: entry.label,
        category:
          typeof entry.category === "string" ? entry.category : "other",
      };
    })
    .filter(
      (entry): entry is { id: string; label: string; category: string } | string => Boolean(entry),
    );

  return normalized;
};

export const asChmsOrgSettingsRecord = (settings: unknown): Record<string, unknown> => {
  return toRecord(settings);
};

export const parseChmsOrgSettings = (
  settings: unknown,
): ChmsOrganizationSettings => {
  const source = toRecord(settings);
  const parsed: ChmsOrganizationSettings = {
    ...source,
    allowed_email_domains: toStringArray(source.allowed_email_domains),
  };

  const normalizedKeys = [
    "allowed_email_domains",
    "plan",
    "mrr",
    "seat_limit",
    "next_billing_date",
    "stripe_customer_id",
    "stripe_subscription_id",
    "allowed_email_domains_enabled",
    "integrations",
    "setup_progress",
    "financeDefaults",
    "payrollPeriod",
    "titles",
    "departments",
    "employmentTypes",
    "compensationBands",
    "householdRelationships",
    "genderIdentities",
    "maritalStatuses",
    "ethnicityCategories",
    "preferredLanguages",
    "leaveTypes",
    "hrRequestCategories",
    "assetClassifications",
    "assetEquipment",
    "assetCampuses",
    "archivedFunds",
    "onboardingProfile",
    "selfRegistration",
    "demoMode",
    // Drop any stale legacy `settings.security` blob. The flat mfa*/session keys
    // below are the canonical source of truth (SettingsSecurity is the sole
    // writer); a leftover nested blob must not survive parse and re-clobber them.
    "security",
    "mfaPolicy",
    "mfaRequiredRoles",
    "mfaRememberDeviceWindowDays",
    "mfaReauthPeriodHours",
    "staffSessionTimeoutHours",
    // W6-4 password policy + MFA grace anchor — normalized below (paired with the
    // reassign blocks so they survive parse rather than being silently dropped).
    "mfaGracePeriodDays",
    "mfaEnforcedAt",
    "minPasswordLength",
    "requireUppercase",
    "requireNumbers",
    "requireSpecialChars",
    "contributionAmountPresets",
    "pledgeCancelRoles",
    "automationNotifications",
    "publicGivingKiosk",
    "campaignFundTracking",
    "messagingScopes",
    "messagingRoleScopes",
    "peopleStatuses",
  ];

  normalizedKeys.forEach((key) => {
    delete (parsed as Record<string, unknown>)[key];
  });

  const plan = toOptionalString(source.plan);
  if (plan) parsed.plan = plan;

  const mrr = toOptionalNumber(source.mrr);
  if (mrr !== undefined) parsed.mrr = mrr;

  const seatLimit = toOptionalNumber(source.seat_limit);
  if (seatLimit !== undefined) parsed.seat_limit = seatLimit;

  const nextBillingDate = toOptionalString(source.next_billing_date);
  if (nextBillingDate) parsed.next_billing_date = nextBillingDate;

  const stripeCustomerId = toOptionalString(source.stripe_customer_id);
  if (stripeCustomerId) parsed.stripe_customer_id = stripeCustomerId;

  const stripeSubscriptionId = toOptionalString(source.stripe_subscription_id);
  if (stripeSubscriptionId) parsed.stripe_subscription_id = stripeSubscriptionId;

  const allowedDomainsEnabled = toOptionalBoolean(source.allowed_email_domains_enabled);
  if (allowedDomainsEnabled !== undefined) parsed.allowed_email_domains_enabled = allowedDomainsEnabled;

  if (Array.isArray(source.allowed_email_domains)) {
    parsed.allowed_email_domains = toStringArray(source.allowed_email_domains);
  }

  const integrations = Array.isArray(source.integrations)
    ? toStringArray(source.integrations)
    : toOptionalRecord(source.integrations);
  if (integrations) parsed.integrations = integrations;

  const setupProgress = toOptionalRecord(source.setup_progress);
  if (setupProgress && Object.keys(setupProgress).length > 0) {
    parsed.setup_progress = setupProgress as Record<string, boolean>;
  }

  const financeDefaults = toOptionalRecord(source.financeDefaults);
  if (financeDefaults) parsed.financeDefaults = financeDefaults;

  const payrollPeriod = toPayrollPeriod(source.payrollPeriod);
  if (payrollPeriod) parsed.payrollPeriod = payrollPeriod;

  const parsedTitles = toOptionalStringArray(source.titles);
  if (parsedTitles) parsed.titles = parsedTitles;

  const departments = toOptionalStringArray(source.departments);
  if (departments) parsed.departments = departments;

  const employmentTypes = toOptionalStringArray(source.employmentTypes);
  if (employmentTypes) parsed.employmentTypes = employmentTypes;

  const compensationBands = toOptionalStringArray(source.compensationBands);
  if (compensationBands) parsed.compensationBands = compensationBands;

  const householdRelationships = toOptionalStringArray(source.householdRelationships);
  if (householdRelationships) parsed.householdRelationships = householdRelationships;

  const genderIdentities = toOptionalStringArray(source.genderIdentities);
  if (genderIdentities) parsed.genderIdentities = genderIdentities;

  const maritalStatuses = toOptionalStringArray(source.maritalStatuses);
  if (maritalStatuses) parsed.maritalStatuses = maritalStatuses;

  const ethnicityCategories = toOptionalStringArray(source.ethnicityCategories);
  if (ethnicityCategories) parsed.ethnicityCategories = ethnicityCategories;

  const preferredLanguages = toOptionalStringArray(source.preferredLanguages);
  if (preferredLanguages) parsed.preferredLanguages = preferredLanguages;

  const leaveTypes = toOptionalStringArray(source.leaveTypes);
  if (leaveTypes) parsed.leaveTypes = leaveTypes;

  const hrRequestCategories = toOptionalStringArray(source.hrRequestCategories);
  if (hrRequestCategories) parsed.hrRequestCategories = hrRequestCategories;

  const assetClassifications = toOptionalStringArray(source.assetClassifications);
  if (assetClassifications) parsed.assetClassifications = assetClassifications;

  const assetEquipment = toOptionalStringArray(source.assetEquipment);
  if (assetEquipment) parsed.assetEquipment = assetEquipment;

  const assetCampuses = toOptionalCampusList(source.assetCampuses);
  if (assetCampuses) parsed.assetCampuses = assetCampuses;

  const archivedFunds = toOptionalStringArray(source.archivedFunds);
  if (archivedFunds) parsed.archivedFunds = archivedFunds;

  const onboardingProfile = toOnboardingProfile(source.onboardingProfile);
  if (onboardingProfile) parsed.onboardingProfile = onboardingProfile;

  const selfRegistration = toOptionalObject(source.selfRegistration);
  if (selfRegistration) parsed.selfRegistration = selfRegistration as {
    enabled?: boolean;
    approvalMode: "manual" | "auto";
  };

  const demoMode = toOptionalObject(source.demoMode);
  if (demoMode) parsed.demoMode = demoMode as { enabled?: boolean };

  if (
    source.mfaPolicy === "optional" ||
    source.mfaPolicy === "required_roles" ||
    source.mfaPolicy === "required_staff"
  ) {
    parsed.mfaPolicy = source.mfaPolicy;
  }

  const mfaRequiredRoles = toOptionalStringRecordArray(source.mfaRequiredRoles);
  if (mfaRequiredRoles) parsed.mfaRequiredRoles = mfaRequiredRoles;

  const mfaRememberDeviceWindowDays = toOptionalNumber(source.mfaRememberDeviceWindowDays);
  if (
    mfaRememberDeviceWindowDays === 0 ||
    mfaRememberDeviceWindowDays === 7 ||
    mfaRememberDeviceWindowDays === 30 ||
    mfaRememberDeviceWindowDays === 90
  ) {
    parsed.mfaRememberDeviceWindowDays = mfaRememberDeviceWindowDays;
  }

  const mfaReauthPeriodHours = toOptionalNumber(source.mfaReauthPeriodHours);
  if (
    mfaReauthPeriodHours === 0 ||
    mfaReauthPeriodHours === 12 ||
    mfaReauthPeriodHours === 24 ||
    mfaReauthPeriodHours === 168 ||
    mfaReauthPeriodHours === 720
  ) {
    parsed.mfaReauthPeriodHours = mfaReauthPeriodHours;
  }

  const staffSessionTimeoutHours = toOptionalNumber(source.staffSessionTimeoutHours);
  if (
    staffSessionTimeoutHours === 4 ||
    staffSessionTimeoutHours === 8 ||
    staffSessionTimeoutHours === 12 ||
    staffSessionTimeoutHours === 24 ||
    staffSessionTimeoutHours === 72 ||
    staffSessionTimeoutHours === 168
  ) {
    parsed.staffSessionTimeoutHours = staffSessionTimeoutHours;
  }

  // W6-4: password policy + MFA grace. Paired with the normalizedKeys entries above.
  const mfaGracePeriodDays = toOptionalNumber(source.mfaGracePeriodDays);
  if (mfaGracePeriodDays !== undefined && mfaGracePeriodDays >= 0 && mfaGracePeriodDays <= 90) {
    parsed.mfaGracePeriodDays = Math.round(mfaGracePeriodDays);
  }

  const mfaEnforcedAt = toOptionalString(source.mfaEnforcedAt);
  if (mfaEnforcedAt) parsed.mfaEnforcedAt = mfaEnforcedAt;

  const minPasswordLength = toOptionalNumber(source.minPasswordLength);
  if (minPasswordLength !== undefined) parsed.minPasswordLength = minPasswordLength;

  const requireUppercase = toOptionalBoolean(source.requireUppercase);
  if (requireUppercase !== undefined) parsed.requireUppercase = requireUppercase;

  const requireNumbers = toOptionalBoolean(source.requireNumbers);
  if (requireNumbers !== undefined) parsed.requireNumbers = requireNumbers;

  const requireSpecialChars = toOptionalBoolean(source.requireSpecialChars);
  if (requireSpecialChars !== undefined) parsed.requireSpecialChars = requireSpecialChars;

  const contributionAmountPresets = toOptionalNumberArray(source.contributionAmountPresets);
  if (contributionAmountPresets) parsed.contributionAmountPresets = contributionAmountPresets;

  // Which roles may cancel a pledge (#7097). Only the list's shape is checked here; resolvePledgeCancelRoles and
  // public.pledge_cancel_roles_for_org decide whether each entry is a role they know, and fall back to the default.
  const pledgeCancelRoles = toOptionalStringRecordArray(source.pledgeCancelRoles);
  if (pledgeCancelRoles) parsed.pledgeCancelRoles = pledgeCancelRoles;

  const automationNotifications = toAutomationNotifications(source.automationNotifications);
  if (automationNotifications) parsed.automationNotifications = automationNotifications as {
    eventRecipients?: string[];
    thresholdRecipients?: string[];
    failureRateThreshold?: number;
    delayMinutesThreshold?: number;
    dailyVolumeThreshold?: number;
  };

  const publicGivingKiosk = toPublicGivingKiosk(source.publicGivingKiosk);
  if (publicGivingKiosk) parsed.publicGivingKiosk = publicGivingKiosk as {
    enabled?: boolean;
    fullScreenEnabled?: boolean;
    idleResetEnabled?: boolean;
    idleResetSeconds?: number;
  };

  const campaignFundTracking = toCampaignFundTracking(source.campaignFundTracking);
  if (campaignFundTracking) parsed.campaignFundTracking = campaignFundTracking as Record<string, unknown>;

  const messagingScopes = toOptionalRecord(source.messagingScopes);
  if (messagingScopes) parsed.messagingScopes = messagingScopes as Record<string, unknown>;

  const messagingRoleScopes = toOptionalRecord(source.messagingRoleScopes);
  if (messagingRoleScopes) parsed.messagingRoleScopes = messagingRoleScopes as Record<string, unknown>;

  const peopleStatuses = toPeopleStatuses(source.peopleStatuses);
  if (peopleStatuses) parsed.peopleStatuses = peopleStatuses;

  return parsed;
};

export const getChmsIntegrationList = (settings: unknown): string[] => {
  const source = parseChmsOrgSettings(settings).integrations;

  if (Array.isArray(source)) {
    return toStringArray(source);
  }

  if (source && isRecord(source)) {
    return Object.keys(source);
  }

  return [];
};

export const getChmsSetupProgress = (settings: unknown): Record<string, boolean> => {
  const source = parseChmsOrgSettings(settings).setup_progress ?? {};

  if (!isRecord(source)) {
    return {};
  }

  return Object.entries(source).reduce<Record<string, boolean>>((acc, [key, value]) => {
    if (typeof value === "boolean") {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const mergeChmsOrgSettings = (
  currentSettings: unknown,
  patch: Record<string, unknown>,
): Record<string, unknown> => ({
  ...asChmsOrgSettingsRecord(currentSettings),
  ...patch,
});
